// Environment configuration for the web frontend
export enum Environment {
  local = 'local',
  staging = 'staging',
  production = 'production',
}

export interface EnvironmentConfig {
  apiUrl: string;
  apiBaseUrl: string;
  apiVersion: string;
  environment: Environment;
  debug: boolean;
  enableAnalytics: boolean;
  enableLogging: boolean;
  googleAnalyticsId?: string;
  sentryDsn?: string;
}

// Environment-specific configurations
const environmentConfigs: Record<Environment, EnvironmentConfig> = {
  [Environment.local]: {
    apiUrl: 'http://localhost:3001/api/v1',
    apiBaseUrl: 'http://localhost:3001',
    apiVersion: 'v1',
    environment: Environment.local,
    debug: true,
    enableAnalytics: false,
    enableLogging: true,
  },
  [Environment.staging]: {
    apiUrl: 'https://api-staging.angi24.com/api/v1',
    apiBaseUrl: 'https://api-staging.angi24.com',
    apiVersion: 'v1',
    environment: Environment.staging,
    debug: true,
    enableAnalytics: true,
    enableLogging: true,
  },
  [Environment.production]: {
    apiUrl: 'https://api.angi24.com/api/v1',
    apiBaseUrl: 'https://api.angi24.com',
    apiVersion: 'v1',
    environment: Environment.production,
    debug: false,
    enableAnalytics: true,
    enableLogging: false,
  },
};

// Get current environment from environment variables
export const getCurrentEnvironment = (): Environment => {
  const env = process.env.REACT_APP_ENV;

  if (env === 'local') return Environment.local;
  if (env === 'staging') return Environment.staging;
  if (env === 'production') return Environment.production;

  // Default to staging if not specified
  return Environment.staging;
};

// Get current configuration
export const getCurrentConfig = (): EnvironmentConfig => {
  const currentEnv = getCurrentEnvironment();
  const config = environmentConfigs[currentEnv];

  // Override with environment variables if they exist
  const resolved = {
    ...config,
    apiUrl: process.env.REACT_APP_API_URL || config.apiUrl,
    apiBaseUrl: process.env.REACT_APP_API_BASE_URL || config.apiBaseUrl,
    apiVersion: process.env.REACT_APP_API_VERSION || config.apiVersion,
    environment: currentEnv,
    debug: process.env.REACT_APP_DEBUG === 'true' || config.debug,
  };

  // Mixed Content: HTTPS page cannot call HTTP API (browser blocks it)
  if (
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:' &&
    resolved.apiUrl.startsWith('http:')
  ) {
    // Misconfiguration — fix REACT_APP_API_URL to use HTTPS in deploy secrets
  }

  return resolved;
};

// Helper functions
export const isLocal = (): boolean =>
  getCurrentEnvironment() === Environment.local;
export const isStaging = (): boolean =>
  getCurrentEnvironment() === Environment.staging;
export const isProduction = (): boolean =>
  getCurrentEnvironment() === Environment.production;

// Get specific configuration values
export const getApiUrl = (): string => getCurrentConfig().apiUrl;
export const getApiBaseUrl = (): string => getCurrentConfig().apiBaseUrl;
export const getApiVersion = (): string => getCurrentConfig().apiVersion;
export const isDebugEnabled = (): boolean => getCurrentConfig().debug;
export const isAnalyticsEnabled = (): boolean =>
  getCurrentConfig().enableAnalytics;
export const isLoggingEnabled = (): boolean => getCurrentConfig().enableLogging;

// Get environment variable with fallback
export const getEnvVar = (
  key: string,
  defaultValue?: string
): string | undefined => {
  return process.env[key] || defaultValue;
};

// Validate required environment variables
export const validateEnvironment = (): void => {
  // No-op — env is resolved at build time via REACT_APP_* variables
};

// Export current config as default
export default getCurrentConfig;
